import React, { FC, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  Divider,
  Grid,
} from '@mui/material';
import {
  Devices as DevicesIcon,
  SwapHoriz as SwapIcon,
  History as HistoryIcon,
  InfoOutlined as InfoIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  getActiveDeploymentDevice,
  getDeploymentDeviceHistory,
  assignDeviceToDeployment,
  replaceDeploymentDevice,
  retrieveDeviceFromDeployment,
  DeploymentDeviceAssignment,
  DeviceCustodyRecord,
  // AI-GENERATED-START
  getEligibleDeploymentDevices,
  reportDamagedDevice,
  reportMissingDevice,
  // AI-GENERATED-END
} from 'src/api2/deviceCustodyApi';
import { useAppState } from 'src/state-management/state-provider';
import { useSnackbar, SnackbarType } from 'src/view/components/SnackbarService';
import { formatDate } from 'src/util/date-utils';

// Import local images for devices
import audioImg from 'src/resources/images/audio.png';
import cameratrapImg from 'src/resources/images/cameratrap.png';
import hydroImg from 'src/resources/images/hydro.png';

const getDeviceImage = (assignment: DeploymentDeviceAssignment | null): string => {
  if (!assignment) return '';
  const text = `${assignment.device_name || ''} ${assignment.device_asset_tag || ''}`.toUpperCase();
  if (text.includes('AUD')) {
    return audioImg;
  }
  if (text.includes('CAM') || text.includes('IMG') || text.includes('IMAGE')) {
    return cameratrapImg;
  }
  if (text.includes('HYD') || text.includes('HYDRO')) {
    return hydroImg;
  }
  return `https://api.dicebear.com/7.x/shapes/svg?seed=${assignment.device_id}`;
};

interface DeploymentDeviceCardProps {
  deploymentId: string;
  projectId: string;
}

const DeploymentDeviceCard: FC<DeploymentDeviceCardProps> = ({
  deploymentId,
  projectId,
}) => {
  const { state } = useAppState();
  const { showSnackbar } = useSnackbar();

  // Dialog States
  // AI-GENERATED-START
  const [swapDialogOpen, setSwapDialogOpen] = useState(false);
  // AI-GENERATED-END
  const [retrieveDialogOpen, setRetrieveDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  // AI-GENERATED-START
  const [damagedDialogOpen, setDamagedDialogOpen] = useState(false);
  const [missingDialogOpen, setMissingDialogOpen] = useState(false);
  // AI-GENERATED-END

  // Data States
  const [activeDevice, setActiveDevice] = useState<DeploymentDeviceAssignment | null>(null);
  const [history, setHistory] = useState<DeploymentDeviceAssignment[]>([]);
  const [projectDevices, setProjectDevices] = useState<DeviceCustodyRecord[]>([]);

  // Loading States
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // AI-GENERATED-START
  const [loadingDevices, setLoadingDevices] = useState(false);
  // AI-GENERATED-END

  // Form States
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [notes, setNotes] = useState('');

  const loadData = async () => {
    setLoading(true);
    try {
      const [device, histResp] = await Promise.all([
        getActiveDeploymentDevice(deploymentId),
        getDeploymentDeviceHistory(deploymentId),
      ]);
      setActiveDevice(device);
      setHistory(histResp.data);
    } catch (error) {
      console.error('Failed to load deployment device info:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (deploymentId) {
      loadData();
    }
  }, [deploymentId]);

  const loadProjectDevices = async () => {
    // AI-GENERATED-START
    setLoadingDevices(true);
    setProjectDevices([]);
    // AI-GENERATED-END
    try {
      // AI-GENERATED-START
      const eligibleDevices = await getEligibleDeploymentDevices(deploymentId);
      const safeRecords = (eligibleDevices || []).map(dev => ({
        custody_id: dev.device_id,
        device_id: dev.device_id,
        project_id: projectId,
        assigned_at: new Date().toISOString(),
        returned_at: null,
        device: dev
      }));
      setProjectDevices(safeRecords);
      if (safeRecords.length > 0) {
        setSelectedDeviceId(safeRecords[0].device_id);
      }
      // AI-GENERATED-END
    } catch (error) {
      console.error('Failed to load project custody devices:', error);
    // AI-GENERATED-START
    } finally {
      setLoadingDevices(false);
    // AI-GENERATED-END
    }
  };

  // AI-GENERATED-START
  // Auto-load eligible devices when there is no active device
  useEffect(() => {
    if (deploymentId && !activeDevice && !loading) {
      loadProjectDevices();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deploymentId, activeDevice, loading]);
  // AI-GENERATED-END

  const handleOpenSwap = () => {
    setNotes('');
    loadProjectDevices();
    setSwapDialogOpen(true);
  };

  const handleOpenRetrieve = () => {
    setNotes('');
    setRetrieveDialogOpen(true);
  };

  const handleOpenHistory = async () => {
    setHistoryDialogOpen(true);
    try {
      const histResp = await getDeploymentDeviceHistory(deploymentId);
      setHistory(histResp.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAssign = async () => {
    if (!selectedDeviceId) return;
    setSubmitting(true);
    try {
      const userId = state.user?.user_id || '00000000-0000-0000-0000-000000000000';
      await assignDeviceToDeployment(deploymentId, {
        device_id: selectedDeviceId,
        assigned_by: userId,
        notes: notes,
      });
      showSnackbar({
        message: 'Device assigned to deployment successfully',
        type: SnackbarType.SUCCESS,
      });
      setSelectedDeviceId('');
      setNotes('');
      await loadData();
    } catch (error: any) {
      console.error(error);
      const detail = error?.data?.detail || error?.message || 'Failed to assign device';
      showSnackbar({
        message: typeof detail === 'string' ? detail : 'Failed to assign device',
        type: SnackbarType.ERROR,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSwap = async () => {
    if (!selectedDeviceId) return;
    setSubmitting(true);
    try {
      const userId = state.user?.user_id || '00000000-0000-0000-0000-000000000000';
      await replaceDeploymentDevice(deploymentId, {
        new_device_id: selectedDeviceId,
        assigned_by: userId,
        notes: notes,
      });
      showSnackbar({
        message: 'Device swapped successfully',
        type: SnackbarType.SUCCESS,
      });
      setSwapDialogOpen(false);
      await loadData();
    } catch (error: any) {
      console.error(error);
      const detail = error?.data?.detail || error?.message || 'Failed to swap device';
      showSnackbar({
        message: typeof detail === 'string' ? detail : 'Failed to swap device',
        type: SnackbarType.ERROR,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetrieve = async () => {
    setSubmitting(true);
    try {
      await retrieveDeviceFromDeployment(deploymentId, {
        notes: notes,
      });
      showSnackbar({
        message: 'Device retrieved from deployment successfully',
        type: SnackbarType.SUCCESS,
      });
      setRetrieveDialogOpen(false);
      await loadData();
    } catch (error: any) {
      console.error(error);
      const detail = error?.data?.detail || error?.message || 'Failed to retrieve device';
      showSnackbar({
        message: typeof detail === 'string' ? detail : 'Failed to retrieve device',
        type: SnackbarType.ERROR,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // AI-GENERATED-START
  const handleOpenDamaged = () => {
    setNotes('');
    loadProjectDevices();
    setDamagedDialogOpen(true);
  };

  const handleOpenMissing = () => {
    setNotes('');
    loadProjectDevices();
    setMissingDialogOpen(true);
  };

  const handleReportDamaged = async () => {
    setSubmitting(true);
    try {
      const payload: { replacement_device_id?: string; notes?: string } = {
        notes: notes,
      };
      if (selectedDeviceId) {
        payload.replacement_device_id = selectedDeviceId;
      }
      await reportDamagedDevice(deploymentId, payload);
      showSnackbar({
        message: 'Device reported as DAMAGED successfully',
        type: SnackbarType.SUCCESS,
      });
      setDamagedDialogOpen(false);
      await loadData();
    } catch (error: any) {
      console.error(error);
      const detail = error?.data?.detail || error?.message || 'Failed to report damaged device';
      showSnackbar({
        message: typeof detail === 'string' ? detail : 'Failed to report damaged device',
        type: SnackbarType.ERROR,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReportMissing = async () => {
    setSubmitting(true);
    try {
      const payload: { replacement_device_id?: string; notes?: string } = {
        notes: notes,
      };
      if (selectedDeviceId) {
        payload.replacement_device_id = selectedDeviceId;
      }
      await reportMissingDevice(deploymentId, payload);
      showSnackbar({
        message: 'Device reported as MISSING successfully',
        type: SnackbarType.SUCCESS,
      });
      setMissingDialogOpen(false);
      await loadData();
    } catch (error: any) {
      console.error(error);
      const detail = error?.data?.detail || error?.message || 'Failed to report missing device';
      showSnackbar({
        message: typeof detail === 'string' ? detail : 'Failed to report missing device',
        type: SnackbarType.ERROR,
      });
    } finally {
      setSubmitting(false);
    }
  };
  // AI-GENERATED-END

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress sx={{ color: '#10B981' }} size={24} />
      </Box>
    );
  }

  return (
    <Card
      sx={{
        bgcolor: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        borderRadius: '16px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 800, color: '#10B981', letterSpacing: '1px', textTransform: 'uppercase' }}>
            Device Deployment Status
          </Typography>
          <IconButton size="small" onClick={handleOpenHistory} sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#FFF' } }}>
            <HistoryIcon fontSize="small" />
          </IconButton>
        </Box>

        {activeDevice ? (
          /* Active Device Details */
          <Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <Avatar
                src={getDeviceImage(activeDevice)}
                sx={{ width: 48, height: 48, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}
              />
              <Box>
                <Typography sx={{ color: '#FFF', fontWeight: 700, fontSize: '15px' }}>
                  {activeDevice.device_name || 'Active Device'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block', mt: 0.25 }}>
                  Tag: {activeDevice.device_asset_tag || '—'}
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block' }}>Type</Typography>
                <Chip
                  label={activeDevice.device_status || 'AUDIO/CAMERA'}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.05)',
                    color: '#FFF',
                    fontWeight: 700,
                    fontSize: '10px',
                    borderRadius: '4px',
                    mt: 0.5,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block' }}>Condition</Typography>
                <Chip
                  label={activeDevice.device_condition || 'NORMAL'}
                  size="small"
                  sx={{
                    bgcolor: activeDevice.device_condition === 'NORMAL' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                    color: activeDevice.device_condition === 'NORMAL' ? '#10B981' : '#EF4444',
                    fontWeight: 700,
                    fontSize: '10px',
                    borderRadius: '4px',
                    mt: 0.5,
                  }}
                />
              </Grid>
            </Grid>

            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block', mb: 0.5 }}>Assigned At</Typography>
            <Typography variant="body2" sx={{ color: '#FFF', fontWeight: 600, fontSize: '13px' }}>
              {formatDate(activeDevice.assigned_at)}
            </Typography>
            
            {activeDevice.notes && (
              <Box sx={{ mt: 1.5, p: 1, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: '6px', borderLeft: '2px solid #10B981' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>
                  "{activeDevice.notes}"
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          /* ── Unassigned State: inline device picker ── */
          // AI-GENERATED-START
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <DevicesIcon sx={{ color: '#10B981', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>
                Select a device to assign
              </Typography>
            </Box>

            {loadingDevices ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                <CircularProgress sx={{ color: '#10B981' }} size={24} />
              </Box>
            ) : projectDevices.length === 0 ? (
              <Box sx={{
                p: 2,
                bgcolor: 'rgba(239, 68, 68, 0.04)',
                border: '1px solid rgba(239, 68, 68, 0.15)',
                borderRadius: '10px',
                display: 'flex',
                gap: 1.5,
                alignItems: 'flex-start',
              }}>
                <WarningIcon sx={{ color: '#EF4444', fontSize: 18, mt: 0.2 }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>
                  No eligible devices found in the project inventory. Check out devices to this project first.
                </Typography>
              </Box>
            ) : (
              <>
                <TextField
                  select
                  fullWidth
                  label="Select Device"
                  value={selectedDeviceId}
                  onChange={e => setSelectedDeviceId(e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#151515',
                      color: '#FFF',
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#2a2a2a' },
                      '&:hover fieldset': { borderColor: '#10B981' },
                      '&.Mui-focused fieldset': { borderColor: '#10B981' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#10B981' },
                  }}
                >
                  {projectDevices.map(pd => (
                    <MenuItem key={pd.device_id} value={pd.device_id}
                      sx={{ color: '#FFF', bgcolor: '#151515', '&:hover': { bgcolor: '#1e1e1e' } }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#FFF' }}>
                          {pd.device?.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                          {pd.device?.human_readable_id} · {pd.device?.serial_number}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  label="Notes (optional)"
                  placeholder="Deployment notes, condition remarks..."
                  multiline
                  rows={2}
                  size="small"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#151515',
                      color: '#FFF',
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#2a2a2a' },
                      '&:hover fieldset': { borderColor: '#444' },
                      '&.Mui-focused fieldset': { borderColor: '#10B981' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#10B981' },
                  }}
                />
              </>
            )}
          </Box>
          // AI-GENERATED-END
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 'auto' }}>
          {activeDevice ? (
            <>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleOpenSwap}
                  startIcon={<SwapIcon />}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.1)',
                    color: '#FFF',
                    textTransform: 'none',
                    borderRadius: '10px',
                    fontWeight: 600,
                    fontSize: '13px',
                    '&:hover': { borderColor: '#10B981', color: '#10B981' },
                  }}
                >
                  Swap Device
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleOpenRetrieve}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.05)',
                    color: '#FFF',
                    border: '1px solid rgba(255,255,255,0.1)',
                    textTransform: 'none',
                    borderRadius: '10px',
                    fontWeight: 700,
                    fontSize: '13px',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  Retrieve
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleOpenDamaged}
                  sx={{
                    borderColor: 'rgba(239, 68, 68, 0.3)',
                    color: '#EF4444',
                    textTransform: 'none',
                    borderRadius: '10px',
                    fontWeight: 600,
                    fontSize: '12px',
                    '&:hover': { borderColor: '#EF4444', bgcolor: 'rgba(239, 68, 68, 0.05)' },
                  }}
                >
                  Report Damaged
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleOpenMissing}
                  sx={{
                    borderColor: 'rgba(245, 158, 11, 0.3)',
                    color: '#F59E0B',
                    textTransform: 'none',
                    borderRadius: '10px',
                    fontWeight: 600,
                    fontSize: '12px',
                    '&:hover': { borderColor: '#F59E0B', bgcolor: 'rgba(245, 158, 11, 0.05)' },
                  }}
                >
                  Report Missing
                </Button>
              </Box>
            </>
          ) : (
            // AI-GENERATED-START
            <Button
              variant="contained"
              fullWidth
              onClick={handleAssign}
              startIcon={submitting ? undefined : <DevicesIcon />}
              disabled={submitting || !selectedDeviceId || loadingDevices || projectDevices.length === 0}
              sx={{
                bgcolor: '#10B981',
                color: '#000',
                textTransform: 'none',
                borderRadius: '10px',
                fontWeight: 700,
                fontSize: '13px',
                '&:hover': { bgcolor: '#059669' },
                '&:disabled': { bgcolor: 'rgba(16,185,129,0.3)', color: 'rgba(0,0,0,0.4)' },
              }}
            >
              {submitting ? <CircularProgress size={16} sx={{ color: '#000' }} /> : 'Assign Device'}
            </Button>
            // AI-GENERATED-END
          )}
        </Box>
      </CardContent>

      {/* Dialog: Assign Device — removed; replaced by inline selector above */}

      {/* Dialog: Swap Device */}
      <Dialog
        open={swapDialogOpen}
        onClose={() => setSwapDialogOpen(false)}
        PaperProps={{
          sx: { bgcolor: '#111', color: '#FFF', border: '1px solid #222', borderRadius: '16px', maxWidth: '400px', width: '100%' },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Swap Active Device</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: '10px !important' }}>
          {projectDevices.length === 0 ? (
            <Box sx={{ p: 2, bgcolor: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '8px', display: 'flex', gap: 1.5 }}>
              <WarningIcon sx={{ color: '#EF4444', mt: 0.25 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                There are no other available devices in the project inventory. Please check out devices to the project first.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ p: 1.5, bgcolor: 'rgba(59,130,246,0.05)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '8px', display: 'flex', gap: 1.5 }}>
                <InfoIcon sx={{ color: '#3B82F6', mt: 0.25 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
                  Swapping will automatically set a return date on the current device and start a new assignment.
                </Typography>
              </Box>
              <TextField
                select
                fullWidth
                label="New Device"
                value={selectedDeviceId}
                onChange={e => setSelectedDeviceId(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#151515', color: '#FFF', borderRadius: '8px',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#444' },
                    '&.Mui-focused fieldset': { borderColor: '#10B981' },
                  },
                }}
              >
                {projectDevices.map(pd => (
                  <MenuItem key={pd.device_id} value={pd.device_id}>
                    {pd.device?.name} ({pd.device?.human_readable_id})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="Notes (Reason for swap)"
                placeholder="e.g. Battery replacement, malfunction..."
                multiline
                rows={2}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#151515', color: '#FFF', borderRadius: '8px',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#444' },
                    '&.Mui-focused fieldset': { borderColor: '#10B981' },
                  },
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #222' }}>
          <Button onClick={() => setSwapDialogOpen(false)} sx={{ color: '#AAA', textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSwap}
            disabled={submitting || projectDevices.length === 0}
            sx={{ bgcolor: '#10B981', color: '#000', fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#059669' } }}
          >
            {submitting ? <CircularProgress size={16} /> : 'Swap'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Retrieve Device */}
      <Dialog
        open={retrieveDialogOpen}
        onClose={() => setRetrieveDialogOpen(false)}
        PaperProps={{
          sx: { bgcolor: '#111', color: '#FFF', border: '1px solid #222', borderRadius: '16px', maxWidth: '400px', width: '100%' },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Retrieve Physical Device</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: '10px !important' }}>
          <Box sx={{ p: 1.5, bgcolor: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '8px', display: 'flex', gap: 1.5 }}>
            <WarningIcon sx={{ color: '#EF4444', mt: 0.25 }} />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
              Are you sure you want to retrieve this device? This will close the deployment assignment.
            </Typography>
          </Box>
          <TextField
            fullWidth
            label="Condition Notes upon Retrieval"
            placeholder="e.g. Returned normal, screen scratched, battery dead..."
            multiline
            rows={2}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#151515', color: '#FFF', borderRadius: '8px',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#444' },
                '&.Mui-focused fieldset': { borderColor: '#10B981' },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #222' }}>
          <Button onClick={() => setRetrieveDialogOpen(false)} sx={{ color: '#AAA', textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRetrieve}
            disabled={submitting}
            sx={{ bgcolor: '#EF4444', color: '#FFF', fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#DC2626' } }}
          >
            {submitting ? <CircularProgress size={16} /> : 'Retrieve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI-GENERATED-START */}
      {/* Dialog: Report Damaged Device */}
      <Dialog
        open={damagedDialogOpen}
        onClose={() => setDamagedDialogOpen(false)}
        PaperProps={{
          sx: { bgcolor: '#111', color: '#FFF', border: '1px solid #222', borderRadius: '16px', maxWidth: '400px', width: '100%' },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#EF4444' }}>Report Device Damaged</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: '10px !important' }}>
          <Box sx={{ p: 1.5, bgcolor: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '8px', display: 'flex', gap: 1.5 }}>
            <WarningIcon sx={{ color: '#EF4444', mt: 0.25 }} />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
              Reporting this device as damaged will update its physical status to DAMAGED and close the current assignment.
            </Typography>
          </Box>
          <TextField
            select
            fullWidth
            label="Optional Replacement Device"
            value={selectedDeviceId}
            onChange={e => setSelectedDeviceId(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#151515', color: '#FFF', borderRadius: '8px',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#444' },
                '&.Mui-focused fieldset': { borderColor: '#EF4444' },
              },
            }}
          >
            <MenuItem value="">
              <em>None (Leave Unassigned)</em>
            </MenuItem>
            {projectDevices.map(pd => (
              <MenuItem key={pd.device_id} value={pd.device_id}>
                {pd.device?.name} ({pd.device?.human_readable_id})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Damage Notes"
            placeholder="Describe the physical damage..."
            multiline
            rows={2}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#151515', color: '#FFF', borderRadius: '8px',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#444' },
                '&.Mui-focused fieldset': { borderColor: '#EF4444' },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #222' }}>
          <Button onClick={() => setDamagedDialogOpen(false)} sx={{ color: '#AAA', textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleReportDamaged}
            disabled={submitting}
            sx={{ bgcolor: '#EF4444', color: '#FFF', fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#DC2626' } }}
          >
            {submitting ? <CircularProgress size={16} /> : 'Report Damaged'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Report Missing Device */}
      <Dialog
        open={missingDialogOpen}
        onClose={() => setMissingDialogOpen(false)}
        PaperProps={{
          sx: { bgcolor: '#111', color: '#FFF', border: '1px solid #222', borderRadius: '16px', maxWidth: '400px', width: '100%' },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#F59E0B' }}>Report Device Missing</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: '10px !important' }}>
          <Box sx={{ p: 1.5, bgcolor: 'rgba(245, 158, 11, 0.05)', border: '1px solid rgba(245, 158, 11, 0.2)', borderRadius: '8px', display: 'flex', gap: 1.5 }}>
            <WarningIcon sx={{ color: '#F59E0B', mt: 0.25 }} />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
              Reporting this device as missing will mark it as MISSING and close the current assignment.
            </Typography>
          </Box>
          <TextField
            select
            fullWidth
            label="Optional Replacement Device"
            value={selectedDeviceId}
            onChange={e => setSelectedDeviceId(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#151515', color: '#FFF', borderRadius: '8px',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#444' },
                '&.Mui-focused fieldset': { borderColor: '#F59E0B' },
              },
            }}
          >
            <MenuItem value="">
              <em>None (Leave Unassigned)</em>
            </MenuItem>
            {projectDevices.map(pd => (
              <MenuItem key={pd.device_id} value={pd.device_id}>
                {pd.device?.name} ({pd.device?.human_readable_id})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Missing Notes"
            placeholder="e.g. Lost in transit, stolen, cannot locate at site..."
            multiline
            rows={2}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#151515', color: '#FFF', borderRadius: '8px',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#444' },
                '&.Mui-focused fieldset': { borderColor: '#F59E0B' },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #222' }}>
          <Button onClick={() => setMissingDialogOpen(false)} sx={{ color: '#AAA', textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleReportMissing}
            disabled={submitting}
            sx={{ bgcolor: '#F59E0B', color: '#FFF', fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#D97706' } }}
          >
            {submitting ? <CircularProgress size={16} /> : 'Report Missing'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* AI-GENERATED-END */}

      {/* Dialog: History / Audit Trail */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        PaperProps={{
          sx: { bgcolor: '#111', color: '#FFF', border: '1px solid #222', borderRadius: '20px', maxWidth: '500px', width: '100%' },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Assignment Audit Trail
          <IconButton onClick={() => setHistoryDialogOpen(false)} sx={{ color: '#666' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ maxHeight: '400px', overflowY: 'auto' }}>
          {history.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', py: 4 }}>
              No history found for this deployment.
            </Typography>
          ) : (
            <List sx={{ p: 0 }}>
              {history.map((record, index) => (
                <Box key={record.deployment_device_id}>
                  {index > 0 && <Divider sx={{ bgcolor: 'rgba(255,255,255,0.05)', my: 1 }} />}
                  <ListItem sx={{ p: 1.5, alignItems: 'flex-start' }}>
                    <ListItemAvatar>
                      <Avatar
                        src={`https://api.dicebear.com/7.x/shapes/svg?seed=${record.device_id}`}
                        sx={{ width: 36, height: 36, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '6px' }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography sx={{ color: '#FFF', fontWeight: 700, fontSize: '14px' }}>
                          {record.device_name}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block' }}>
                            Asset Tag: {record.device_asset_tag} | SN: {record.device_status || '—'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block', mt: 0.5 }}>
                            Deployed: {formatDate(record.assigned_at)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: record.returned_at ? 'rgba(255,255,255,0.3)' : '#10B981', display: 'block' }}>
                            Returned: {record.returned_at ? formatDate(record.returned_at) : 'Active now'}
                          </Typography>
                          {record.notes && (
                            <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 1, p: 0.75, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: '4px' }}>
                              Notes: {record.notes}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </Box>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default DeploymentDeviceCard;
